import axios from 'axios'
import queryString from 'query-string'
import type {
  ExpenseCategoryFiltersRequestParams,
  ExpenseCategoryLabelResponse,
  ExpenseCategoryResponse,
} from 'src/queries/organizations/expenseCategories.queries'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

export type ExpenseCategoriesResponse = {
  expenseCategories: ExpenseCategoryResponse[]
}

// These read functions intentionally return `response.data` and let axios
// errors propagate untouched, so the preserved useQueryXxx wrappers can
// reproduce useExtendQuery's exact `error: axiosError.response?.data` contract.
// A normalizing boundary (e.g. getResponseData) would make `.error` truthy on
// network failures and change consumer gating — e.g. ExpenseCategoryLabels,
// which renders a NotFound page whenever this hook's `.error` is truthy.

export const getExpenseCategories = async (
  filters: ExpenseCategoryFiltersRequestParams,
): Promise<ExpenseCategoriesResponse> => {
  const { data } = await axios.get<ExpenseCategoriesResponse>(
    `/expensedata/categories?${queryString.stringify(filters)}`,
    getRequestConfig(RequestConfig.Default),
  )
  return data
}

export const getExpenseCategory = async (
  expenseCategoryId: string,
): Promise<ExpenseCategoryResponse> => {
  const { data } = await axios.get<ExpenseCategoryResponse>(
    `/expensedata/categories/${expenseCategoryId}`,
    getRequestConfig(RequestConfig.Default),
  )
  return data
}

export const getExpenseCategoryLabel = async (
  expenseCategoryId: string,
  labelId: string,
): Promise<ExpenseCategoryLabelResponse> => {
  const { data } = await axios.get<ExpenseCategoryLabelResponse>(
    `/expensedata/categories/${expenseCategoryId}/labels/${labelId}`,
    getRequestConfig(RequestConfig.Default),
  )
  return data
}

export const getTransactionCustomFields = async (
  transactionId: string,
): Promise<ExpenseCategoriesResponse> => {
  const { data } = await axios.get<ExpenseCategoriesResponse>(
    `/expensedata/categories?${queryString.stringify({
      transactionId,
      active: true,
    })}`,
    getRequestConfig(RequestConfig.Default),
  )
  return data
}
